import React, { useCallback } from 'react';
import { Backdrop, Box, Button, Dialog, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { throttle } from 'lodash';

const AlertDialog = ({ open, handleClose, onYesClick, disableButtons, itemToDelete }) => {
	const debouncedOnYesClick = useCallback(throttle(onYesClick, 300), [itemToDelete]);
	const itemName = itemToDelete?.name;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			slots={{ backdrop: Backdrop }}
			slotProps={{
				backdrop: { sx: { backgroundColor: 'rgba(15, 23, 42, 0.4) !important' } },
			}}
			PaperProps={{
				sx: {
					borderRadius: '12px',
					boxShadow: '0 8px 40px rgba(15, 23, 42, 0.18)',
					maxWidth: '400px',
					width: '100%',
					m: 2,
				},
			}}
		>
			<Box sx={{ p: '28px' }}>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
					<Box
						sx={{
							width: 36,
							height: 36,
							borderRadius: '8px',
							backgroundColor: '#FEF2F2',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
							mt: '1px',
						}}
					>
						<WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#B91C1C' }} />
					</Box>
					<Box>
						<Typography
							id="alert-dialog-title"
							sx={{
								fontWeight: 600,
								fontSize: '15px',
								lineHeight: '22px',
								color: '#0F172A',
								fontFamily: 'Open Sans, sans-serif',
							}}
						>
							{itemName ? `Delete "${itemName}"?` : 'Confirm deletion'}
						</Typography>
						<Typography
							id="alert-dialog-description"
							sx={{
								mt: '6px',
								fontSize: '13.5px',
								lineHeight: '20px',
								color: '#64748B',
								fontFamily: 'Open Sans, sans-serif',
							}}
						>
							{itemName
								? `"${itemName}" will be permanently deleted. This action cannot be undone.`
								: 'This action is permanent and cannot be undone.'}
						</Typography>
					</Box>
				</Box>

				<Box
					sx={{
						mt: '24px',
						pt: '16px',
						borderTop: '1px solid #F1F5F9',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '8px',
					}}
				>
					<Button
						onClick={handleClose}
						disabled={disableButtons}
						sx={{
							fontSize: '13.5px',
							fontWeight: 500,
							fontFamily: 'Open Sans, sans-serif',
							color: '#475569',
							border: '1px solid #E2E8F0',
							borderRadius: '7px',
							px: '16px',
							py: '7px',
							textTransform: 'none',
							'&:hover': { backgroundColor: '#F8FAFC', borderColor: '#CBD5E1' },
						}}
					>
						Cancel
					</Button>
					<LoadingButton
						variant="contained"
						onClick={debouncedOnYesClick}
						disabled={disableButtons}
						loading={disableButtons}
						sx={{
							fontSize: '13.5px',
							fontWeight: 600,
							fontFamily: 'Open Sans, sans-serif',
							backgroundColor: '#B91C1C',
							color: '#FFFFFF',
							borderRadius: '7px',
							px: '16px',
							py: '7px',
							textTransform: 'none',
							boxShadow: 'none',
							'&:hover': { backgroundColor: '#991B1B', boxShadow: 'none' },
							'&.Mui-disabled': { backgroundColor: '#FECACA', color: '#FFFFFF' },
							'& .MuiLoadingButton-loadingIndicator': { color: '#FFFFFF' },
						}}
					>
						<span>Delete</span>
					</LoadingButton>
				</Box>
			</Box>
		</Dialog>
	);
};

export default AlertDialog;
